import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fmtBRL } from '@/lib/utils';
import type { CategoriaSnapshot } from '@/lib/hooks/prestacoes';

interface CategoriaProgressBarProps {
  categoria: CategoriaSnapshot;
  className?: string;
}

const STATUS_META: Record<
  CategoriaSnapshot['status'],
  { label: string; barClass: string; textClass: string; icon: React.ReactNode }
> = {
  OK: {
    label: 'Dentro do teto',
    barClass: 'bg-mint-deep',
    textClass: 'text-mint-deep',
    icon: <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.8} />,
  },
  WARN: {
    label: 'Proximo do teto',
    barClass: 'bg-warn',
    textClass: 'text-warn',
    icon: <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.8} />,
  },
  BLOCK: {
    label: 'Teto excedido',
    barClass: 'bg-block',
    textClass: 'text-block',
    icon: <XCircle className="h-3.5 w-3.5" strokeWidth={1.8} />,
  },
};

const STATUS_BG: Record<CategoriaSnapshot['status'], string> = {
  OK: 'bg-mint-soft/60',
  WARN: 'bg-warn-soft/60',
  BLOCK: 'bg-block-soft/60',
};

export function CategoriaProgressBar({ categoria, className }: CategoriaProgressBarProps) {
  const meta = STATUS_META[categoria.status];
  const pct = Math.min(categoria.percentual, 100);

  return (
    <div
      className={cn(
        'rounded-2 border border-card-stroke p-3 transition-colors',
        STATUS_BG[categoria.status],
        className,
      )}
      role="group"
      aria-label={`Categoria ${categoria.label}: ${pct.toFixed(1)}%`}
    >
      {/* Linha topo: label + status */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[13px] font-semibold text-fg leading-tight">{categoria.label}</span>
        <span className={cn('inline-flex items-center gap-1 text-[11px] font-mono font-semibold', meta.textClass)}>
          {meta.icon}
          {meta.label}
        </span>
      </div>

      {/* Barra de progresso */}
      <div
        className="h-2 w-full rounded-full bg-card-stroke overflow-hidden"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${pct.toFixed(1)}% utilizado`}
      >
        <div
          className={cn('h-full rounded-full transition-all duration-500', meta.barClass)}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Linha fundo: valores + denominador */}
      <div className="flex items-center justify-between gap-2 mt-1.5">
        <span className="text-[11px] font-mono text-fg-muted">
          {fmtBRL(categoria.total)} / {fmtBRL(categoria.denominador)}
        </span>
        <span className="text-[11px] font-mono font-bold text-fg">
          {pct.toFixed(1)}%
        </span>
      </div>

      <p className="text-[10px] text-fg-dim font-mono mt-0.5">
        denominador: {categoria.denominadorCodigo}
      </p>
    </div>
  );
}
